/**
 * Cost Calculator
 * 
 * Handles calculation of all costs involved in car arbitrage:
 * - Transport costs between countries
 * - Registration fees
 * - Import taxes (delegates to country-specific modules)
 */

import calculatePortugalISV from '../tax-calculators/calculatePortugalISV.js';
import calculateSpainIEDMT from '../tax-calculators/calculateSpainIEDMT.js';

/**
 * Transport costs between European countries (in euros)
 * Based on typical car transport service rates
 */
const TRANSPORT_COSTS = {
  'DE-PT': 850,  // Germany to Portugal
  'PT-DE': 850,  // Portugal to Germany
  'DE-ES': 650,  // Germany to Spain
  'ES-DE': 650,  // Spain to Germany
  'PT-ES': 400,  // Portugal to Spain
  'ES-PT': 400,  // Spain to Portugal
  'DE-FR': 500,  // Germany to France
  'FR-DE': 500,  // France to Germany
  'ES-FR': 350,  // Spain to France
  'FR-ES': 350,  // France to Spain
  'PT-FR': 700,  // Portugal to France
  'FR-PT': 700   // France to Portugal
};

/**
 * Calculate transport cost between two countries
 * 
 * @param {string} fromCountry - Source country code (e.g., 'DE', 'ES', 'PT')
 * @param {string} toCountry - Destination country code
 * @returns {number} Transport cost in euros
 * 
 * @example
 * calculateTransportCost('DE', 'PT') → 850
 * calculateTransportCost('ES', 'PT') → 400
 * calculateTransportCost('FR', 'IT') → 500 (default for unknown routes)
 */
export function calculateTransportCost(fromCountry, toCountry) {
  const route = `${fromCountry}-${toCountry}`;
  return TRANSPORT_COSTS[route] || 500; // Default €500 for unknown routes
}

/**
 * Calculate registration fee for target country
 * 
 * @param {string} targetCountry - Country code where car will be registered
 * @returns {number} Registration fee in euros
 * 
 * @example
 * calculateRegistrationFee('PT') → 350
 * calculateRegistrationFee('ES') → 350
 */
export function calculateRegistrationFee(targetCountry) {
  // Standard EU registration fee
  // Can be customized per country if needed
  const fees = {
    'PT': 350,
    'ES': 350,
    'DE': 350,
    'FR': 350
  };
  
  return fees[targetCountry] || 350;
}

/**
 * Calculate import tax for target country
 * Delegates to country-specific tax calculators
 * 
 * @param {Object} car - Car object with properties (year, fuel_type, engine_cc, co2_emissions_wltp, co2_emissions_nedc, etc.)
 * @param {string} targetCountry - Target country code
 * @returns {number} Import tax in euros
 * 
 * @example
 * calculateImportTax({ year: 2018, fuel_type: 'petrol', ... }, 'PT') → 2518
 * calculateImportTax({ year: 2020, fuel_type: 'diesel', ... }, 'DE') → 0
 */
export function calculateImportTax(car, targetCountry) {
  // Portugal: ISV (Imposto Sobre Veículos)
  if (targetCountry === 'PT') {
    return calculatePortugalISV(car);
  }
  
  // Germany: No import tax within EU
  if (targetCountry === 'DE') {
    return 0;
  }
  
  // Spain: IEDMT (Impuesto de Matriculación) — registration tax based on CO2
  if (targetCountry === 'ES') {
    return calculateSpainIEDMT(car);
  }
  
  // France: Add French tax calculation when needed
  // if (targetCountry === 'FR') {
  //   return calculateFranceTax(car);
  // }
  
  // Default: No import tax within EU
  return 0;
}

/**
 * Calculate total cost of acquiring and importing a car
 * 
 * @param {Object} car - Car object with at least { price, year, fuel_type, ... }
 * @param {string} sourceCountry - Source country code
 * @param {string} targetCountry - Target country code
 * @returns {Object} Breakdown of all costs
 * 
 * @example
 * calculateTotalCost(car, 'DE', 'PT')
 * → {
 *     purchasePrice: 30000,
 *     transport: 850,
 *     importTax: 2518,
 *     registration: 350,
 *     total: 33718
 *   }
 */
export function calculateTotalCost(car, sourceCountry, targetCountry) {
  const transportCost = calculateTransportCost(sourceCountry, targetCountry);
  const importTax = calculateImportTax(car, targetCountry);
  const registrationFee = calculateRegistrationFee(targetCountry);
  
  const total = car.price + transportCost + importTax + registrationFee;
  
  return {
    purchasePrice: car.price,
    transport: transportCost,
    importTax: importTax,
    registration: registrationFee,
    total: total
  };
}
